import { useMemo } from "react";

function cn(...values) {
  return values.filter(Boolean).join(" ");
}

function defaultValueFormatter(value) {
  return new Intl.NumberFormat("nl-NL").format(Number(value || 0));
}

export function buildStatusOverview(statuses = []) {
  const normalized = (Array.isArray(statuses) ? statuses : [])
    .map((status) => ({
      key: String(status?.key || "").trim(),
      label: String(status?.label || "").trim() || "Onbekend",
      value: Number(status?.value || 0),
      count:
        status?.count === null || status?.count === undefined
          ? null
          : Number(status.count),
      colorClass: String(status?.colorClass || "").trim() || "bg-slate-300",
      legendColorClass:
        String(status?.legendColorClass || "").trim() ||
        String(status?.colorClass || "").trim() ||
        "bg-slate-300",
    }))
    .filter((status) => status.key && Number.isFinite(status.value) && status.value >= 0);

  const total = normalized.reduce((sum, status) => sum + status.value, 0);

  const visibleSegments = normalized
    .filter((status) => status.value > 0)
    .map((status) => ({
      ...status,
      percentage: total > 0 ? (status.value / total) * 100 : 0,
    }));

  return {
    total,
    statuses: normalized,
    segments: visibleSegments,
  };
}

export default function StatusOverviewCard({
  title,
  subtitle = "In huidige selectie",
  statuses = [],
  totalOverride = null,
  totalSuffix = "",
  totalPrefix = "",
  valueFormatter = defaultValueFormatter,
  onStatusClick,
  className = "",
  legendValueLabel = "Waarde",
  emptyLabel = "Geen statusdata beschikbaar.",
}) {
  const overview = useMemo(() => buildStatusOverview(statuses), [statuses]);
  const totalValue = totalOverride !== null && totalOverride !== undefined
    ? Number(totalOverride || 0)
    : overview.total;

  const formattedTotal = `${totalPrefix}${valueFormatter(totalValue)}${totalSuffix}`;

  return (
    <section className={cn("rounded-2xl border border-slate-200 bg-white p-5 shadow-sm", className)}>
      <header className="mb-4">
        <p className="text-base font-semibold text-slate-900">{title}</p>
        <p className="text-sm text-slate-500">{subtitle}</p>
        <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
          {formattedTotal}
        </p>
      </header>

      <div className="mb-4">
        {overview.segments.length > 0 ? (
          <div className="h-3 overflow-hidden rounded-full bg-slate-100">
            <div className="flex h-full w-full">
              {overview.segments.map((segment) => (
                <div
                  key={segment.key}
                  className={cn("h-full", segment.colorClass)}
                  style={{ width: `${segment.percentage}%` }}
                  title={`${segment.label}: ${valueFormatter(segment.value)} (${segment.percentage.toFixed(1)}%)`}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">{emptyLabel}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {overview.statuses.map((status) => {
          const percentage = overview.total > 0 ? (status.value / overview.total) * 100 : 0;
          const countLabel =
            status.count !== null && Number.isFinite(status.count)
              ? ` (${valueFormatter(status.count)})`
              : "";

          const Component = onStatusClick ? "button" : "div";
          return (
            <Component
              key={status.key}
              type={onStatusClick ? "button" : undefined}
              onClick={onStatusClick ? () => onStatusClick(status.key) : undefined}
              className={cn(
                "min-w-[180px] grow rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left",
                onStatusClick
                  ? "transition hover:border-slate-300 hover:bg-slate-100"
                  : ""
              )}
              title={`${status.label}: ${legendValueLabel} ${valueFormatter(status.value)} (${percentage.toFixed(1)}%)`}
            >
              <div className="flex items-center gap-2">
                <span className={cn("h-2.5 w-2.5 rounded-full", status.legendColorClass)} />
                <span className="truncate text-sm font-medium text-slate-700">
                  {status.label}
                  {countLabel}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {legendValueLabel}: {valueFormatter(status.value)}
                </span>
                <span>{percentage.toFixed(1)}%</span>
              </div>
            </Component>
          );
        })}
      </div>
    </section>
  );
}
